import React from "react";
import { Typography, Box } from "@mui/material";

const ConvertingGuide = () => {
  return (
    <>
      <Typography variant="h6" className="section-heading" color="secondary">
        How Charts Translate to Written Instructions
      </Typography>
      <Typography variant="body1">
        Every chart can be converted to written instructions. The process involves reading each row
        of the chart and writing out what each symbol means in text form. Loops & Purls does this
        automatically, but understanding the relationship helps you read both formats more
        effectively.
      </Typography>

      <Typography variant="h6" className="section-heading" color="secondary">
        Basic Conversion Example
      </Typography>
      <Typography variant="body1">
        Here's how a simple chart row translates to written instructions:
      </Typography>
      <Box className="mb-3">
        <Typography variant="body2" fontWeight="medium" className="mb-1">
          Chart Row (reading right to left):
        </Typography>
        <Box
          sx={{
            fontFamily: "Roboto Mono, monospace",
            fontSize: "1.5rem",
            textAlign: "center",
            backgroundColor: "var(--lp-cream)",
            padding: "0.75rem",
            borderRadius: "8px",
            border: "1px solid var(--lp-divider)",
          }}
        >
          ◊ ◊ ● ● ◊ ◊
        </Box>
        <Typography variant="body2" fontWeight="medium" className="mb-1 mt-2">
          Written Instructions:
        </Typography>
        <Box
          component="pre"
          className="pattern-example"
          sx={{
            fontFamily: "Roboto Mono, monospace",
            backgroundColor: "var(--lp-cream)",
            padding: "1rem",
            borderRadius: "8px",
          }}
        >
          K2, P2, K2
        </Box>
      </Box>

      <Typography variant="h6" className="section-heading" color="secondary">
        Understanding the Relationship
      </Typography>
      <ul className="feature-list">
        <li>
          <span className="feature-item-title">One Symbol = One Stitch</span>
          <span className="feature-item-description">
            Each square in a chart represents exactly one stitch. When converting, you read each
            symbol and write its abbreviation.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Groups and Repeats</span>
          <span className="feature-item-description">
            When the same symbol appears multiple times in a row, written instructions group them
            (e.g., 'K5' instead of 'K, K, K, K, K'). Repeats are indicated with asterisks.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Row Context</span>
          <span className="feature-item-description">
            Written instructions specify whether you're on a right-side or wrong-side row, which
            affects how you read the chart.
          </span>
        </li>
      </ul>

      <Typography variant="h6" className="section-heading" color="secondary">
        Using Loops & Purls for Conversion
      </Typography>
      <Typography variant="body1">
        Loops & Purls automatically converts your chart to written instructions:
      </Typography>
      <ul className="feature-list">
        <li>
          <span className="feature-item-title">Real-Time Conversion</span>
          <span className="feature-item-description">
            As you create your chart, written instructions are generated automatically. You can see
            both formats side by side.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Export Options</span>
          <span className="feature-item-description">
            When exporting, you can choose to include written instructions, the chart, or both.
            This gives you flexibility in how you use your pattern.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Abbreviation Options</span>
          <span className="feature-item-description">
            Choose between full names (Knit, Purl) or abbreviations (K, P) in your written
            instructions, depending on your preference or pattern style.
          </span>
        </li>
      </ul>

      <Typography variant="h6" className="section-heading" color="secondary">
        Why Both Formats Matter
      </Typography>
      <Typography variant="body1">
        Having both chart and written formats available helps you:
      </Typography>
      <ul className="feature-list">
        <li>
          <span className="feature-item-title">Learn Charts</span>
          <span className="feature-item-description">
            If you're new to charts, seeing the written instructions alongside helps you understand
            how charts work.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Verify Your Work</span>
          <span className="feature-item-description">
            You can check your knitting against both formats to catch mistakes more easily.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Share Patterns</span>
          <span className="feature-item-description">
            Different knitters prefer different formats. Providing both makes your patterns accessible
            to more people.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Accessibility</span>
          <span className="feature-item-description">
            Some knitters find written patterns easier to follow, while others prefer charts. Having
            both accommodates different learning styles.
          </span>
        </li>
      </ul>
    </>
  );
};

export default ConvertingGuide;

